import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PortalLayout from "../components/PortalLayout";
import { Send, MessageSquare, User, ChevronLeft } from "lucide-react";

const MessagesPage = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [activeRecipientId, setActiveRecipientId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadData = async () => {
    try {
      const [contactsRes, threadsRes] = await Promise.all([
        authFetch(`${API}/messages/contacts`),
        authFetch(`${API}/messages/threads`),
      ]);
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (threadsRes.ok) setThreads(await threadsRes.json());
    } catch (e) {
      console.error("Error loading messages:", e);
    } finally {
      setLoading(false);
    }
  };

  const openThread = useCallback(async (threadId, recipientId) => {
    setActiveThread(threadId);
    setActiveRecipientId(recipientId);
    try {
      const res = await authFetch(`${API}/messages/thread/${threadId}`);
      if (res.ok) setMessages(await res.json());
    } catch {
      toast.error("Failed to load messages");
    }

    // Start polling
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`${API}/messages/thread/${threadId}`);
        if (res.ok) setMessages(await res.json());
      } catch { /* silent */ }
    }, 5000);
  }, []);

  const startNewThread = (contact) => {
    const participants = [user.user_id, contact.user_id].sort();
    const threadId = `thread_${participants[0]}:::${participants[1]}`;
    openThread(threadId, contact.user_id);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeThread || !activeRecipientId) return;

    setSending(true);
    try {
      const res = await authFetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_id: activeRecipientId, content: newMessage.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages((prev) => [...prev, msg]);
        setNewMessage("");
        loadData(); // Refresh threads sidebar
      }
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Get contact/thread info for sidebar display
  const getContactForThread = (thread) => thread?.other_user;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <PortalLayout title="Messages">

      <div className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-6 py-6">
        <div className="soft-card !p-0 flex overflow-hidden" style={{ height: "calc(100vh - 160px)" }}>
          {/* Sidebar */}
          <div
            className={`w-full sm:w-80 shrink-0 border-r flex flex-col ${activeThread ? "hidden sm:flex" : "flex"}`}
            style={{ borderColor: "var(--border-color)" }}
          >
            <div className="p-4 font-semibold text-base border-b" style={{ color: "var(--text-primary)", borderColor: "var(--border-color)" }}>
              Conversations
            </div>

            {/* Existing threads */}
            <div className="flex-1 overflow-y-auto">
              {threads.map((t) => {
                const c = getContactForThread(t);
                const isActive = activeThread === t.thread_id;
                return (
                  <button
                    key={t.thread_id}
                    onClick={() => openThread(t.thread_id, c?.user_id)}
                    className={`w-full text-left px-5 py-4 border-b border-[var(--border-light)] flex items-center gap-3 transition-all duration-300 ${
                      isActive ? "bg-[var(--primary)]/5" : "hover:bg-[var(--primary)]/5"
                    }`}
                    data-testid={`thread-${t.thread_id}`}
                  >
                    {c?.picture ? (
                      <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover shadow-sm border border-[var(--border-light)]" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] bg-[var(--primary)]/10">
                        <User className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm font-bold truncate text-[var(--text-main)]">
                          {c?.name || "Unknown"}
                        </span>
                        {t.unread_count > 0 && (
                          <span className="bg-[var(--success)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                            {t.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                        {t.last_message}
                      </p>
                    </div>
                  </button>
                );
              })}

              {/* New contact buttons (if no existing thread) */}
              {contacts
                .filter((c) => !threads.some((t) => t.other_user?.user_id === c.user_id))
                .map((c) => (
                  <button
                    key={c.user_id}
                    onClick={() => startNewThread(c)}
                    className="w-full text-left px-5 py-4 border-b border-[var(--border-light)] flex items-center gap-3 hover:bg-[var(--primary)]/5 transition-all duration-300"
                    data-testid={`contact-${c.user_id}`}
                  >
                    {c.picture ? (
                      <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover shadow-sm border border-[var(--border-light)]" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] bg-[var(--primary)]/10">
                        <User className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-bold text-[var(--text-main)]">
                        {c.name}
                      </span>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {c.role === "team_member" ? "Team Member" : c.role === "super_admin" ? "Admin" : "Client"}
                      </p>
                    </div>
                  </button>
                ))}

              {threads.length === 0 && contacts.length === 0 && (
                <div className="text-center py-16 px-4">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#145886]/5 flex items-center justify-center">
                    <MessageSquare className="w-7 h-7 text-[#145886]/25" />
                  </div>
                  <p className="font-medium text-sm mb-1" style={{ color: "var(--text-primary)" }}>No conversations yet</p>
                  <p className="text-xs max-w-[200px] mx-auto" style={{ color: "var(--text-muted)" }}>
                    A dedicated team member will be assigned to you shortly. You'll be able to message them directly here.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!activeThread ? "hidden sm:flex" : "flex"}`}>
            {activeThread ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border-color)" }}>
                  <button
                    className="sm:hidden p-1"
                    onClick={() => { setActiveThread(null); if (pollRef.current) clearInterval(pollRef.current); }}
                  >
                    <ChevronLeft className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
                  </button>
                  <span className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>
                    {(() => {
                      const t = threads.find((th) => th.thread_id === activeThread);
                      return t?.other_user?.name || "Chat";
                    })()}
                  </span>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => {
                    const isMine = msg.sender_id === user?.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] px-5 py-3 rounded-[20px] text-sm shadow-sm ${
                            isMine
                              ? "bg-[var(--primary)] text-white rounded-br-[5px]"
                              : "bg-[var(--background-primary)] border border-[var(--border-light)] text-[var(--text-main)] rounded-bl-[5px]"
                          }`}
                        >
                          <p className="font-medium">{msg.content}</p>
                          <p className={`text-[10px] mt-1 font-bold ${isMine ? "text-white/70" : "text-[var(--text-muted)]"}`}>
                            {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSend} className="p-4 border-t border-[var(--border-light)] flex gap-3 bg-[var(--background-primary)]">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 rounded-full border-[var(--border-light)] bg-white shadow-sm h-12 px-5"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="soft-btn !p-0 w-12 h-12 flex items-center justify-center rounded-full disabled:opacity-50"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5 ml-1" />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center px-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#145886]/5 flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-[#145886]/20" />
                  </div>
                  <p className="font-medium text-sm mb-1" style={{ color: "var(--text-primary)" }}>Select a conversation</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>Choose a contact from the sidebar to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default MessagesPage;
